import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes a website URL by adding https:// if no protocol is present.
 * Returns empty string for empty input.
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Format a number as USD currency string (e.g. "$12.50").
 */
export function formatCurrency(amount: number): string {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(safe);
}

/**
 * Format time until expiry as "X days Y hours" or "Y hours" (if &lt; 24h).
 * Returns empty string if already expired or invalid.
 */
/**
 * Format business/category type for display: replace underscores with spaces and title-case.
 * e.g. "home_bakery" -> "Home Bakery"
 */
export function formatCategory(category: string | null | undefined): string {
  if (category == null || category === '') return '';
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatExpiresIn(expiresAt: string | Date | null | undefined): string {
  if (!expiresAt) return '';
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  if (ms === 0) return '';
  const totalHours = ms / (1000 * 60 * 60);
  const days = Math.floor(totalHours / 24);
  const hours = Math.floor(totalHours % 24);
  if (days > 0) {
    return hours > 0 ? `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}` : `${days} day${days !== 1 ? 's' : ''}`;
  }
  return `${hours || 1} hour${(hours || 1) !== 1 ? 's' : ''}`;
}
